import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { AlertFactory } from '@/features/shell'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from '@/features/admin/bundle/bulk-action-alerts'
import { type Id } from '@/infra/id'

export interface BundleDiscountPreview {
  bundle: Bundle
  currentPrice: number
  newPrice: number
}

export class BulkApplyDiscountDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  @observable
  accessor selectedBundles: Bundle[] = []

  @observable
  accessor percentOff = 0

  readonly validation = validationContext(this, {
    percentOff: [
      func(
        ({ value }) => value > 0 && value <= 100,
        'Enter a percentage greater than 0 and no more than 100.',
      ),
    ],
  })

  constructor(
    private readonly _bundleStore: BundleStore,
    private readonly _newAlert: AlertFactory,
    private readonly _refreshBundles: RefreshBundlesFn,
  ) {}

  /**
   * Archived bundles can't be discounted, so they never make it into the
   * preview or the request.
   */
  @computed
  get eligibleBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  @computed
  get archivedCount() {
    return this.selectedBundles.length - this.eligibleBundles.length
  }

  @computed
  get hasEligibleBundles() {
    return this.eligibleBundles.length > 0
  }

  @computed
  get hasArchivedBundles() {
    return this.archivedCount > 0
  }

  @computed
  get previews(): BundleDiscountPreview[] {
    return this.eligibleBundles.map((bundle) => ({
      bundle,
      currentPrice: bundle.price,
      newPrice: this._newPriceFor(bundle.price),
    }))
  }

  @computed
  get hasDiscount() {
    return this.percentOff > 0
  }

  /**
   * A percentage over 100 drives the price below $0. Those projections block
   * the apply and are called out in red.
   */
  @computed
  get negativePreviews() {
    return this.previews.filter((preview) => preview.newPrice < 0)
  }

  @computed
  get negativePriceWarning() {
    const bundles = this.negativePreviews.map((preview) => preview.bundle)

    if (bundles.length === 0) {
      return undefined
    }

    return `Lower the percentage — ${bundles.length} ${pluralizeBundles(bundles.length)} would fall below $0: ${formatBundleNames(bundles)}.`
  }

  @computed
  get canApply() {
    return (
      this.hasDiscount &&
      this.previews.length > 0 &&
      this.negativePreviews.length === 0
    )
  }

  @action.bound
  open(bundles: Bundle[]) {
    this.reset()
    this.selectedBundles = bundles

    this.modal.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.percentOff = 0

    this.submit.reset()
    this.validation.reset()
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid || !this.canApply) {
      return
    }

    const previews = this.previews

    try {
      const result = await this._bundleStore.bulkEditPrices({
        workspaceId: previews[0].bundle.workspace.id,
        priceUpdates: previews.map((preview) => ({
          bundleId: preview.bundle.id,
          price: preview.newPrice,
        })),
      })

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.archivedIds,
      ])

      showBulkActionResult(
        this._newAlert,
        previews.map((preview) => preview.bundle),
        skippedIds,
        {
          success: (count) =>
            `Discount applied to ${count} ${pluralizeBundles(count)}.`,
          partialTitle: 'Some bundles were skipped',
          partial: (count, skipped) =>
            `Discounted ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
        },
      )
    } catch (e) {
      showBulkActionError(
        this._newAlert,
        e,
        'You do not have permission to apply discounts in this workspace.',
        'There was an error applying the discount.',
      )
      this.modal.close()
      return
    }

    await this._refreshBundles(previews[0].bundle.workspace)

    this.modal.close()
  })

  /**
   * Computed in integer cents so float representation can't shift a price by a
   * cent. The server stores what we send, so this rounding is the only one.
   */
  private _newPriceFor(price: number) {
    const priceCents = Math.round(price * 100)
    const cents = Math.round((priceCents * (100 - this.percentOff)) / 100)

    return cents / 100
  }
}
